import {
  APP_INITIALIZER,
  LOCALE_ID,
  NgModule,
  RendererFactory2,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { UserLayoutComponent } from './components/layouts/user-layout.component';
import { GuestLayoutComponent } from './components/layouts/guest-layout.component';
import { HeaderComponent } from './components/header/header.component';
import { AppRoutingModule } from './app.routing.module';
import { HTTP_INTERCEPTORS, HttpClient, withInterceptors, provideHttpClient } from '@angular/common/http';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { authInterceptorProvider } from './interceptors/auth.interceptor';
import {
  LocalStorageService,
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage,
} from 'ngx-webstorage';
import { environment } from 'src/environments/environment';
import { SocketIoModule } from 'ngx-socket-io';
import { DomHub } from './sockets/dom.hub';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { errorInterceptorProvider } from './interceptors/error.interceptor';
import { clubAanvraagInterceptorProvider } from './interceptors/club-aanvraag.interceptor';
import { ErrorLibrary } from 'siq_errors';
import { FormsModule } from '@angular/forms';
import { teamsInterceptorProvider } from './interceptors/teams.interceptor';
import { taggingInterceptorProvider } from './interceptors/tagging.interceptor';
import { meekijkenInterceptorProvider } from './interceptors/meekijken.interceptor';
import { RecordEventsStorageService } from './modules/record-events/services/record-events-storage.service';
import { HasChangesGuard } from './guards/has-changes.guard';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { zhCnLocale, deLocale, nlLocale, esLocale, frLocale } from 'ngx-bootstrap/locale';
import { registerLocaleData } from '@angular/common';
import localeZh from '@angular/common/locales/zh';
import localeDe from '@angular/common/locales/de';
import localeNl from '@angular/common/locales/nl';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { HeaderTeamsComponent } from './components/header-teams/header-teams.component';
import { GgImageDirective } from './directives/gg-image.directive';
import { TooltipConfig, TooltipModule } from 'ngx-bootstrap/tooltip';

/**
 * Zweefteksten (tooltips) op aanraakschermen helemaal uit (eigenaar 13-09:
 * "ik krijg die afspeellijsten niet verwijderd" op de iPhone). Op iOS vuurt
 * een tik eerst mouseenter; de zweeftekst voegt dan een element aan de
 * pagina toe, en zodra de pagina bij mouseenter verandert laat Safari de
 * klik vallen. Het prullenbakje (en elk ander icoon met een zweeftekst)
 * deed op de telefoon dus niets. Zonder triggers komt er geen luisteraar
 * en dus geen zweeftekst; die stond op de telefoon toch al verborgen.
 */
export function tooltipConfigFactory(): TooltipConfig {
  const config = new TooltipConfig();
  if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) {
    config.triggers = '';
  }
  return config;
}
import { ByteSizePipe } from './pipes/byte-size.pipe';
import { CoreModule } from './core/core.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrModule } from 'ngx-toastr';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { RoutePermissionsService } from './core/services/route-permissions.service';
import { ClubPermissionService } from './services/club-permission.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { AccountDropdownComponent } from './components/account-dropdown/account-dropdown.component';
import { WelkomDialoogComponent } from './components/onboarding/welkom-dialoog/welkom-dialoog.component';
import { RondleidingComponent } from './components/onboarding/rondleiding/rondleiding.component';

// DEFINE LOCALE FOR DATE PIPE AND DATEPICKER
registerLocaleData(localeZh, 'zh');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
defineLocale('zh', zhCnLocale);
defineLocale('de', deLocale);
defineLocale('nl', nlLocale);
defineLocale('es', esLocale);
defineLocale('fr', frLocale);

function createTranslateLoaderFactory(
  httpClient: HttpClient,
): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    UserLayoutComponent,
    GuestLayoutComponent,
    HeaderComponent,
    HeaderTeamsComponent,
  ],
  imports: [
    CoreModule,
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    BsDatepickerModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    BsDropdownModule.forRoot(),
    TooltipModule.forRoot(),
    NgSelectModule,
    SocketIoModule.forRoot({
      url: environment.dispatchUrl,
      options: { autoConnect: false },
    }),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    // Meldingen rechtsonder (eigenaar 14-09: "dan stoort dat niet zo"); op de
    // telefoon net boven de tabbalk (zie _mobile.scss).
    ToastrModule.forRoot({ positionClass: 'toast-bottom-right', timeOut: 4000, progressBar: true, preventDuplicates: true }),
    FormsModule,
    GgImageDirective,
    ByteSizePipe,
    NgCircleProgressModule.forRoot(),
    AccountDropdownComponent,
    WelkomDialoogComponent,
    RondleidingComponent,
  ],
  providers: [
    { provide: TooltipConfig, useFactory: tooltipConfigFactory },
    provideHttpClient(
      withInterceptors([
        // Eerst: `aanvraag:<id>` mag nooit naar arch (helpers/club-aanvraag.ts).
        clubAanvraagInterceptorProvider,
        authInterceptorProvider,
        teamsInterceptorProvider,
        taggingInterceptorProvider,
        errorInterceptorProvider,
        // Na de foutafhandeling: een geweigerd verzoek tijdens meekijken
        // komt daar als 403 met sleutel view_only langs, zonder extra melding.
        meekijkenInterceptorProvider
      ]),
    ),
    provideNgxWebstorage(
      withNgxWebstorageConfig({
        prefix: 'siq',
        separator: '.',
        caseSensitive: true,
      }),
      withLocalStorage(),
      withSessionStorage(),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: (domHub: DomHub) => () => domHub.connect(),
      multi: true,
      deps: [DomHub],
    },
    { provide: ErrorLibrary },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (recordEventsStorageService: RecordEventsStorageService) => () =>
          recordEventsStorageService.clearManualEvents(),
      multi: true,
      deps: [RecordEventsStorageService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (clubPermissionService: ClubPermissionService) => () =>
        clubPermissionService.checkPermission(),
      multi: true,
      deps: [ClubPermissionService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (routerPermissionService: RoutePermissionsService) => () =>
        routerPermissionService.prepareRoutes(),
      multi: true,
      deps: [RoutePermissionsService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (googleAnalyticsService: GoogleAnalyticsService) => () =>
        googleAnalyticsService.injectGoogleAnalytics(),
      multi: true,
      deps: [GoogleAnalyticsService, RendererFactory2],
    },
    {
      provide: LOCALE_ID,
      deps: [LocalStorageService],
      useFactory: (localStorage: LocalStorageService) =>
        localStorage.retrieve('language') ?? environment.defaultLanguage,
    },
    HasChangesGuard,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
